import React from 'react';

export interface ValidationErrors {
    firstName?: string;
    lastName?: string;
    position?: string;
    workspaceName?: string;
    phoneNumber?: string;
    password?: string;
    passwordConfirm?: string;
    key?: string;
    users?: string;
}

export interface ValidationData {
    firstName: string;
    lastName: string;
    position: string;
    workspaceName: string;
    phoneNumber: string;
    password: string;
    passwordConfirm: string;
    key: string;
    users: string[];
}

const PHONE_REGEX = {
    RU: /^\+7\s?\(?[0-9]{3}\)?\s?[0-9]{3}-?[0-9]{2}-?[0-9]{2}$/,
    AM: /^\+374\s?[0-9]{2}\s?[0-9]{3}-?[0-9]{3}$/,
    KZ: /^\+7\s?[0-9]{3}\s?[0-9]{3}-?[0-9]{2}-?[0-9]{2}$/,
};

export class MigrationValidator {
    private static readonly MAX_LENGTH = 40;
    private static readonly MIN_PASSWORD_LENGTH = 3;

    static validateField(fieldName: keyof ValidationData, value: string): string | undefined {
        if (!value || value.trim() === '') {
            return 'Это поле обязательно для заполнения';
        }

        if (value.length > this.MAX_LENGTH) {
            return `Максимальная длина поля ${this.MAX_LENGTH} символов`;
        }

        switch (fieldName) {
            case 'firstName':
            case 'lastName':
                return this.validateName(value);
            case 'position':
                return this.validatePosition(value);
            case 'workspaceName':
                return this.validateWorkspaceName(value);
            case 'phoneNumber':
                return this.validatePhoneNumber(value);
            case 'password':
                return this.validatePassword(value);
            case 'passwordConfirm':
                return this.validatePasswordConfirm(value);
            case 'key':
                return this.validateKey(value);
            default:
                return undefined;
        }
    }

    static validateForm(data: ValidationData): ValidationErrors {
        const errors: ValidationErrors = {};

        Object.keys(data).forEach((key) => {
            if (key !== 'users') {
                const fieldName = key as keyof ValidationData;
                const value = data[fieldName];
                if (typeof value === 'string') {
                    const error = this.validateField(fieldName, value);
                    if (error) {
                        errors[fieldName] = error;
                    }
                }
            }
        });

        if (data.password && data.passwordConfirm && data.password !== data.passwordConfirm) {
            errors.passwordConfirm = 'Пароли не совпадают';
        }

        return errors;
    }

    private static validateName(value: string): string | undefined {
        if (!/^[а-яёa-z\s-]+$/i.test(value)) {
            return 'Имя может содержать только буквы, пробелы и дефисы';
        }
        return undefined;
    }

    private static validatePosition(value: string): string | undefined {
        if (!/^[а-яёa-z0-9\s-]+$/i.test(value)) {
            return 'Должность может содержать только буквы, цифры, пробелы и дефисы';
        }
        return undefined;
    }

    private static validateWorkspaceName(value: string): string | undefined {
        if (!/^[а-яёa-z0-9\s-]+$/i.test(value)) {
            return 'Название рабочего пространства может содержать только буквы, цифры, пробелы и дефисы';
        }
        return undefined;
    }

    private static validatePhoneNumber(value: string): string | undefined {
        const cleanPhone = value.replace(/\D/g, '');

        if (cleanPhone.startsWith('7') && cleanPhone.length === 11) {
            if (!PHONE_REGEX.RU.test(value)) {
                return 'Неверный формат российского номера. Пример: +7 (999) 123-45-67';
            }
            return undefined;
        }

        if (cleanPhone.startsWith('374') && cleanPhone.length === 11) {
            if (!PHONE_REGEX.AM.test(value)) {
                return 'Неверный формат армянского номера. Пример: +374 10 123-456';
            }
            return undefined;
        }

        if (cleanPhone.startsWith('7') && cleanPhone.length === 11) {
            if (!PHONE_REGEX.KZ.test(value)) {
                return 'Неверный формат казахстанского номера. Пример: +7 701 123-45-67';
            }
            return undefined;
        }

        return 'Неверный формат номера телефона. Поддерживаются номера России (+7), Армении (+374), Казахстана (+7)';
    }

    private static validatePassword(value: string): string | undefined {
        if (value.length < this.MIN_PASSWORD_LENGTH) {
            return `Пароль должен содержать минимум ${this.MIN_PASSWORD_LENGTH} символов`;
        }

        return undefined;
    }

    private static validatePasswordConfirm(value: string): string | undefined {
        return undefined;
    }

    private static validateKey(value: string): string | undefined {
        if (!/^[a-z0-9-]+$/i.test(value)) {
            return 'Ключ может содержать только буквы, цифры и дефисы';
        }
        return undefined;
    }

    static formatPhoneNumber(value: string): string {
        const cleanPhone = value.replace(/\D/g, '');

        if (cleanPhone.startsWith('7') && cleanPhone.length === 11) {
            const match = cleanPhone.match(/^7(\d{3})(\d{3})(\d{2})(\d{2})$/);
            if (match) {
                return `+7 ${match[1]} ${match[2]}-${match[3]}-${match[4]}`;
            }
        }

        if (cleanPhone.startsWith('374') && cleanPhone.length === 11) {
            const match = cleanPhone.match(/^374(\d{2})(\d{3})(\d{3})$/);
            if (match) {
                return `+374 ${match[1]} ${match[2]}-${match[3]}`;
            }
        }

        return value;
    }

    static getPhoneCountry(phone: string): 'RU' | 'AM' | 'KZ' | null {
        const cleanPhone = phone.replace(/\D/g, '');

        if (cleanPhone.startsWith('374')) {
            return 'AM';
        }

        if (cleanPhone.startsWith('7')) {
            return 'RU';
        }

        return null;
    }

    static normalizeEmail(email: string): string {
        return email.toLowerCase().trim();
    }
}
